import { FC, useEffect } from "react";

import {GetServerSideProps, InferGetServerSidePropsType as Infer} from "next";
import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import LayoutHead from "@components/PageHeader/LayoutHead";
import withPropsMiddleware from "@lib/withPropsMiddleware";
import { loadFeatures } from "@modules/features";
import Layout, { TWithLayout } from "@modules/layout";
import { loadStaticOrderData, OrderPage, OrderStep, ORDER_MESSAGE_ZONE } from "@modules/order";
import { QUERY_PARAMS } from "@utils/constants";
import type {TStaticInitialOrderData} from "@modules/order/types";
import loadWeightRegulations from "@modules/weight-regulations/services/loadWeightRegulations";

type TWithLocales = {
    defaultLocale: string;
    customLocales: string[];
    customLocale: string; // Change from string? to string
    features: string[] | null;
    orderData: TStaticInitialOrderData;
    hasPaymentFailure: boolean;
    weightRegulations: string[] | null;
};

export const getServerSideProps: GetServerSideProps<TWithLocales> = withPropsMiddleware(
  async ({
    locale,
    req: {
      headers: { host },
    },
    query,
  }) => {
    const hasPaymentFailure = query?.[QUERY_PARAMS.ORDER_PAYMENT_SUCCESS] === "false";

    const orderDataPromise = loadStaticOrderData({
      locale: locale!,
      hostname: host!,
      isPaymentFailed: hasPaymentFailure,
    });

    const [orderData] = await Promise.all([orderDataPromise]);

    const features = locale ? await loadFeatures(locale, host) : null;

      const weightRegulations = locale? await loadWeightRegulations(locale, host) : null;

    return {
      props: { orderData, hasPaymentFailure, features, defaultLocale: '', customLocales: [], customLocale: '', weightRegulations  },
    };
  }
);

const OrderSummaryPage: TWithLayout<FC<Infer<typeof getServerSideProps>>> = ({ orderData, hasPaymentFailure, weightRegulations }) => {
  const { t } = useTranslation();

  const setMessage = useMessage();

  useEffect(() => {
    if (hasPaymentFailure) {
      setMessage({
        message: t("order-message-payment_failure"),
        props: { variant: "error", icon: <Icon icon="warning" size="1.875rem" />, isAnimated: false, canClose: true },
        zone: ORDER_MESSAGE_ZONE,
      });
    }
  }, [hasPaymentFailure, setMessage, t]);

  return (
    <>
      <LayoutHead title={t("pages-order_summary-title")} description={t("pages-order_summary-description")} />
      <OrderPage currentStep={OrderStep.Summary} orderData={orderData} weightRegulations={weightRegulations} />
    </>
  );
};

OrderSummaryPage.Layout = <Layout hasMobileFooter={false} />;

export default OrderSummaryPage;
